'use client'

import { SafeResponse } from "@/schemas/safe-response-interface"
import { Subscription } from "@/schemas/subscription"
import { api } from "@/trpc/react"
import { Dispatch, createContext, useContext, useReducer, useState } from "react"
import { toast } from "react-toastify"

export type SubscriptionAction = (
  {type: 'set-subscription', subscription: Subscription | null} | 
  {type: 'set-alias', alias: string } | 
  {type: 'set-city', city: string } | 
  {type: 'set-state', state: string} | 
  {type: 'set-zipcode', zipcode: number} | 
  {type: 'set-street1', street1: string} | 
  {type: 'set-street2', street2: string } | 
  {type: 'toggle-prompt'}
)

export type SubscriptionState = {
  subscription: Subscription | null,
  deletePromptOpen: boolean
}

export type Status = 'pending' | 'loading' | 'valid' | 'invalid' | 'error' | 'success'

export type SubscriptionHook = {
  state: SubscriptionState, 
  dispatch: Dispatch<SubscriptionAction>, 
  findAlias: (alias:string)=>Promise<Status>, 
  aliasStatus: Status, 
  status: Status, 
  invalidate: ()=>Promise<void>
  invalidateAlias: ()=>Promise<void>
  readSubscription: ()=>Promise<Subscription | null>,
  updateSubscription: (fields: {
    alias?:string,
    city?:string,
    state?:string,
    zipcode?:number,
    street1?:string,
    street2?:string
  })=>Promise<{type:'success'} | {type:'error', message:string}>
  cancelSubscription: ()=>Promise<SafeResponse>
}

function subscriptionReducer(state:SubscriptionState, action: SubscriptionAction){
  const copy = {...state} 
  if (action.type === 'set-subscription'){
    copy.subscription = action.subscription
    return copy
  }
  if (copy.subscription === null){
    return copy
  }
  switch(action.type){
    case 'set-alias':
      copy.subscription.alias = action.alias
    break; 
    case 'set-city': 
      copy.subscription.city = action.city
    break; 
    case 'set-street1': 
      copy.subscription.street1 = action.street1
    break; 
    case 'set-street2': 
      copy.subscription.street2 = action.street2
    break; 
    case 'set-zipcode': 
      copy.subscription.zipcode = action.zipcode
    break; 
    case 'set-state': 
      copy.subscription.state = action.state
    break; 
    case 'toggle-prompt': 
      copy.deletePromptOpen = !copy.deletePromptOpen
    break; 

  }
  return copy
}

const initialState:SubscriptionState = {
  subscription: null, 
  deletePromptOpen: false
}

const SubscriptionContext = createContext<null | SubscriptionHook>(null)

export function SubscriptionProvidcer({children}:{children:React.ReactNode}){
  const [state, dispatch] = useReducer(subscriptionReducer, initialState)
  const {mutateAsync: apiUpdateSubscription} = api.subscribe.updateSubscription.useMutation()
  const {mutateAsync: apiReadSubscription} = api.subscribe.readSubscription.useMutation()
  const {mutateAsync: apiCancelSubscription} = api.stripe.cancelSubscription.useMutation()
  const [status, setStatus] = useState<Status>('loading')
  const [aliasStatus, setAliasStatus] = useState<Status>('pending')

  async function findAlias(alias:string){
    setAliasStatus('loading')
    const res = await apiReadSubscription({alias: alias})
    switch(res.type){
      case 'error': 
        toast.error(res.message)
        setAliasStatus('pending')
        return 'pending'
      break; 
      case 'success': 
        if (res.subscription && res.subscription.realtor_sub !== state.subscription?.realtor_sub){
          setAliasStatus('invalid')
          return 'invalid'
        }else{
          setAliasStatus('valid')
          return 'valid'
        }
      break; 
    }
  }

  async function updateSubscription(fields: {
    alias?:string, 
    city?:string, 
    state?:string, 
    zipcode?:number, 
    street1?:string, 
    street2?:string 
  }){
    setStatus('loading')
    const res = await apiUpdateSubscription(fields)
    switch(res.type){
      case 'error': 
        toast.error(res.message)
        setStatus('error')
      break; 
      case 'success':
        toast.success('updated details')
        setStatus('success')
      break;
    }
    return res
  }

  async function readSubscription(){
    setStatus('loading')
    const res = await apiReadSubscription({})
    let subscription = null
    switch(res.type){
      case 'error': 
        toast.error(res.message)
        setStatus('error')
      break; 
      case  'success': 
        subscription = res.subscription
        setStatus('success')
      break; 
    }
    return subscription
  }

  async function cancelSubscription(){
    setStatus('loading')
    return await apiCancelSubscription()
  }

  async function invalidateAlias(){
    setAliasStatus('pending')
  }

  async function invalidate(){
    setStatus('pending')
  }


  return (
    <SubscriptionContext.Provider value={{invalidate, invalidateAlias, aliasStatus, status, state, dispatch, findAlias, updateSubscription, readSubscription, cancelSubscription}}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export function useSubscription(){
  const context = useContext(SubscriptionContext)
  if (!context){
    throw new Error('Missing subscription context')
  }
  return context
}
